import React, { useCallback, useEffect, useRef, useState } from 'react';
import TrainingFilterCheck from '../../components/TrainingFilterCheck';
import { reviewApi } from '../../utils/reviewApi';
import { filterListingUrl } from '../../utils/orcanosLinks';
import { Modal, SearchSelect } from './reviewUi';

/**
 * New / Edit panel (mockup screen 7): a name, a project and ONE Orcanos DMS filter.
 *
 * The filter is checked for its six columns (D4) as soon as it is picked, and the panel
 * can't be saved while a required column is missing — an unselected Routing State would
 * read as "not Draft" on every row and silently hide Start review everywhere (Note #34).
 * A check that could not RUN (Orcanos error, empty filter) never blocks: it is shown as
 * "not checked", not as an all-clear, and the user may still save.
 */
export default function PanelFormModal({ panelId, onClose, onSaved }) {
  const editing = !!panelId;
  const [setup, setSetup] = useState(null);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [filters, setFilters] = useState({ available: true, filters: [], loading: false });
  const [filterId, setFilterId] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [mapping, setMapping] = useState({});
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const token = useRef(0);

  // Load projects (and, when editing, the saved config) once.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const s = await reviewApi.setup();
        if (!live) return;
        setSetup(s);
        if (editing) {
          const c = await reviewApi.panelConfig(panelId);
          if (!live) return;
          setName(c.name || '');
          setProjectId(String(c.project_id || ''));
          setVersionId(String(c.version_id || ''));
          setFilterId(String(c.filter_id || ''));
          setIsPublic(!!c.is_public);
          setMapping(c.fields || {});
        } else if ((s.projects || []).length === 1) {
          setProjectId(s.projects[0].project_id);
          setVersionId(s.projects[0].version_id || '');
        }
      } catch (e) {
        if (live) setError(e.message);
      }
    })();
    return () => { live = false; };
  }, [editing, panelId]);

  const project = (setup?.projects || []).find((p) => p.project_id === projectId);

  // Filters of the chosen project.
  useEffect(() => {
    if (!projectId) return undefined;
    let live = true;
    setFilters((f) => ({ ...f, loading: true }));
    reviewApi.filters(projectId, project?.type_label || '')
      .then((d) => { if (live) setFilters({ available: d.available, filters: d.filters || [], reason: d.reason, loading: false }); })
      .catch(() => { if (live) setFilters({ available: false, filters: [], loading: false, reason: 'error' }); });
    return () => { live = false; };
  }, [projectId, project?.type_label]);

  const runCheck = useCallback(async (fid, ver, map) => {
    if (!fid || !ver) { setCheck(null); return; }
    const mine = ++token.current;
    setChecking(true);
    try {
      const d = await reviewApi.checkFilter({ filter_id: String(fid), version_id: String(ver), fields: map || {} });
      if (mine === token.current) setCheck(d.result);
    } catch (e) {
      if (mine === token.current) setCheck({ checked: false, reason: 'error', message: e.message });
    } finally {
      if (mine === token.current) setChecking(false);
    }
  }, []);

  useEffect(() => { runCheck(filterId, versionId, mapping); }, [filterId, versionId, mapping, runCheck]);

  const pickProject = (pid) => {
    const p = (setup?.projects || []).find((x) => x.project_id === pid);
    setProjectId(pid);
    setVersionId(p?.version_id || '');
    setFilterId('');
    setCheck(null);
  };

  const blocked = !!(check && check.checked && !check.ok);
  const filterName = (filters.filters || []).find((f) => String(f.id) === String(filterId))?.name || '';

  const save = async () => {
    setError('');
    if (!name.trim()) { setError('Give the panel a name.'); return; }
    if (!projectId || !versionId) { setError('Pick a project.'); return; }
    if (!filterId) { setError('Pick an Orcanos filter.'); return; }
    if (blocked) { setError('Add the missing columns to the filter in Orcanos first, then check again.'); return; }
    setBusy(true);
    const body = { name: name.trim(), project_id: projectId, version_id: versionId,
      filter_id: String(filterId), filter_name: filterName, is_public: isPublic, fields: mapping };
    try {
      const res = editing ? await reviewApi.updatePanel(panelId, body) : await reviewApi.createPanel(body);
      onSaved(res.id || panelId);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const filterLink = filterListingUrl(setup?.orcanos_url, versionId, 'DMS', filterId);

  return (
    <Modal
      title={editing ? 'Edit panel' : 'New panel'}
      subtitle="A panel lists the DMS items one Orcanos filter returns."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--lg" onClick={() => runCheck(filterId, versionId, mapping)}
            disabled={!filterId || checking}>Check again</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save}
            disabled={busy || blocked || !setup}>{busy ? 'Saving…' : 'Save panel'}</button>
        </>
      )}
    >
      {!setup && !error && <div className="drv-muted">Loading projects from Orcanos…</div>}
      <label className="drv-field">
        <span className="drv-lbl">Panel name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. SOPs & work instructions" maxLength={120} autoFocus />
      </label>

      <div className="drv-row">
        <label className="drv-field">
          <span className="drv-lbl">Project</span>
          <select className="drv-select" value={projectId} onChange={(e) => { pickProject(e.target.value); setError(''); }}>
            <option value="">— pick a project —</option>
            {(setup?.projects || []).map((p) => (
              <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
            ))}
          </select>
        </label>
        <label className="drv-field" style={{ flexGrow: 2 }}>
          <span className="drv-lbl">Orcanos filter (DMS)</span>
          {filters.available || filters.loading ? (
            <SearchSelect
              ariaLabel="Orcanos filter"
              disabled={!projectId || filters.loading}
              value={filterId}
              onChange={(v) => { setFilterId(String(v)); setError(''); }}
              placeholder={filters.loading ? 'Loading filters…' : projectId ? 'Search filters…' : 'Pick a project first'}
              options={[
                ...(filterId && !(filters.filters || []).some((f) => String(f.id) === String(filterId))
                  ? [{ value: String(filterId), label: `#${filterId} (current)` }] : []),
                ...(filters.filters || []).map((f) => ({ value: String(f.id), label: `${f.name} (#${f.id})` })),
              ]}
            />
          ) : (
            <input className="drv-input" inputMode="numeric" placeholder="Filter ID"
              value={filterId} onChange={(e) => setFilterId(e.target.value.replace(/\D/g, ''))} />
          )}
        </label>
      </div>
      {filterLink && (
        <a className="drv-link" href={filterLink} target="_blank" rel="noopener noreferrer"
          style={{ marginTop: -8 }}>Open this filter in Orcanos ↗</a>
      )}

      <div className="drv-field">
        <span className="drv-lbl">Filter columns <span>— Key, Name, File name, Revision, Routing state,
          Last approved revision</span></span>
        <TrainingFilterCheck
          result={check}
          busy={checking}
          hasFilter={!!filterId}
          mapping={mapping}
          onMap={(role, col) => setMapping((m) => {
            const next = { ...m };
            if (col) next[role] = col; else delete next[role];
            return next;
          })}
          onRecheck={() => runCheck(filterId, versionId, mapping)}
        />
      </div>

      <div className="drv-field">
        <span className="drv-lbl">Who sees this panel</span>
        <label className="drv-chk">
          <input type="radio" name="drv-vis" checked={isPublic} onChange={() => setIsPublic(true)} />
          <span><strong>Everyone in the account</strong></span>
        </label>
        <label className="drv-chk">
          <input type="radio" name="drv-vis" checked={!isPublic} onChange={() => setIsPublic(false)} />
          <span><strong>Only me</strong></span>
        </label>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}
